import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { ArrowDownRight } from "lucide-react";
import { MaskLine } from "./Reveal";
import { scrollToId } from "./Nav";
import { useLang } from "@/lib/i18n";
import { useSite } from "@/lib/site";

const PILLAR_IDS = ["courses", "gear", "killhouse"];

export const Hero = () => {
  const { t } = useLang();
  const photos = useSite().imgs("hero");
  const [active, setActive] = useState(0);
  useEffect(() => {
    if (photos.length < 2) return undefined;
    const id = setInterval(() => setActive((a) => (a + 1) % photos.length), 6000);
    return () => clearInterval(id);
  }, [photos.length]);
  const lines = t("hero.lines");
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [4, -4]), { stiffness: 60, damping: 20 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-5, 5]), { stiffness: 60, damping: 20 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="top" ref={ref} data-testid="hero-section" onMouseMove={onMove}
      className="group relative min-h-[100svh] overflow-hidden [perspective:1200px]">
      <motion.div style={{ y, rotateX: rx, rotateY: ry }} className="absolute -inset-[6%] will-change-transform">
        <motion.div
          initial={{ scale: 1.25, opacity: 0 }}
          animate={{ scale: 1.05, opacity: 1 }}
          transition={{ duration: 2.4, ease: [0.16, 1, 0.3, 1] }}
          className="relative w-full h-full"
        >
          {photos.map((src, i) => (
            <img key={src + i} src={src} alt={i === active ? "A.R.R.O.W operators in training" : ""} data-testid={`hero-photo-${i}`}
              className={`absolute inset-0 w-full h-full object-cover object-[50%_35%] grayscale brightness-[0.55] group-hover:grayscale-0 transition-[filter,opacity] duration-1000 ${i === active % photos.length ? "opacity-100" : "opacity-0"}`} />
          ))}
        </motion.div>
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)] via-[var(--ink)]/30 to-[var(--ink)]/60" />
      <div className="absolute inset-0 grid-lines opacity-[0.12]" />
      <motion.img src="/img/logo.png" alt="" aria-hidden="true"
        style={{ rotateX: rx, rotateY: ry }}
        initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 0.9, scale: 1 }} transition={{ delay: 1, duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
        className="hidden lg:block absolute right-12 top-32 z-10 w-[22vw] max-w-[340px] drop-shadow-[0_20px_60px_rgba(0,0,0,0.9)]" />

      <motion.div style={{ y: textY }} className="relative z-10 flex flex-col justify-end min-h-[100svh] px-5 sm:px-8 lg:px-12 pb-36 sm:pb-40 pt-28">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
          className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--bone)]/80 mb-6 flex items-center gap-3">
          <span className="w-2 h-2 bg-[var(--signal)] blink" /> {t("hero.tag")}
        </motion.p>
        <h1 data-testid="hero-headline" className="font-display font-black uppercase leading-[0.82] tracking-[-0.01em] text-[19vw] sm:text-[15vw] lg:text-[11.5vw]">
          <MaskLine onLoad delay={0.35}>{lines[0]}</MaskLine>
          <MaskLine onLoad delay={0.5}>{lines[1]}</MaskLine>
          <MaskLine onLoad delay={0.65} className="text-outline">{lines[2]}</MaskLine>
        </h1>
        <div className="mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.8 }}
            className="max-w-md text-sm sm:text-base text-[var(--bone)]/85 leading-relaxed">
            {t("hero.text")}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.35, duration: 0.8 }}
            className="flex flex-wrap gap-3">
            <button data-testid="hero-cta-courses" onClick={() => scrollToId("courses")}
              className="group/cta clip-cut-sm bg-[var(--signal)] text-[var(--ink)] font-mono text-xs uppercase tracking-[0.2em] px-6 py-4 inline-flex items-center gap-3 hover:bg-[var(--bone)] transition-colors">
              {t("hero.ctaCourses")} <ArrowDownRight className="w-4 h-4 transition-transform group-hover/cta:rotate-[-45deg]" />
            </button>
            <button data-testid="hero-cta-waitlist" onClick={() => scrollToId("killhouse")}
              className="border border-[var(--bone)]/40 font-mono text-xs uppercase tracking-[0.2em] px-6 py-4 hover:border-[var(--signal)] hover:text-[var(--signal)] transition-colors">
              {t("hero.ctaKillhouse")}
            </button>
          </motion.div>
        </div>
      </motion.div>

      <div className="absolute bottom-0 inset-x-0 z-10 grid grid-cols-3 border-t border-[var(--line)] bg-[var(--ink)]/70 backdrop-blur-md">
        {PILLAR_IDS.map((id, i) => (
          <motion.button key={id} data-testid={`hero-pillar-${id}`} onClick={() => scrollToId(id)}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 + i * 0.12 }}
            className="text-left px-4 sm:px-8 lg:px-12 py-4 sm:py-6 border-r last:border-r-0 border-[var(--line)] hover:bg-[var(--ink-3)] transition-colors">
            <span className="font-mono text-[10px] sm:text-xs text-[var(--signal)]">0{i + 1}</span>
            <span className="block font-display text-base sm:text-2xl font-bold uppercase mt-1 hover:translate-x-1 transition-transform">{t("hero.pillars")[i]}</span>
          </motion.button>
        ))}
      </div>
    </section>
  );
};
